import { useState } from 'react'
import { EyeOff, FileUp, ImageUp, Image as ImageIcon, Upload } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ActionPopover } from '@/components/action-popover'
import {
  correctionDetail,
  correctionErrorMessage,
  mediaIdOf,
  staffHiddenRows,
  useProductMediaCorrections,
  useSetProductMediaHidden,
  useUploadProductMedia,
  type MediaCorrection,
  type MediaKind,
} from '@/api/product-corrections'
import type { components } from '@/api/schema'

type ProductImage = components['schemas']['ProductImageRead']

const REASON_NOTE = { label: 'Reason', placeholder: 'Why? (optional)', singleLine: true }

function noun(kind: MediaKind) {
  return kind === 'image' ? 'photo' : 'spec sheet'
}

export function MediaHideButton({
  productId,
  kind,
  mediaId,
  name,
}: {
  productId: string
  kind: MediaKind
  mediaId: number
  name: string
}) {
  const hide = useSetProductMediaHidden(productId)
  return (
    <ActionPopover
      align="end"
      title={`Hide this ${noun(kind)}`}
      trigger={
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground"
          aria-label={`Hide ${name}`}
          title={`Hide ${noun(kind)}`}
        >
          <EyeOff className="h-4 w-4" />
        </Button>
      }
      note={REASON_NOTE}
      confirmLabel="Hide"
      onConfirm={(reason, close) => {
        // The page hides it at once and puts it back if the save fails.
        hide.mutate(
          { kind, mediaId, hidden: true, reason: reason || null },
          {
            onError: (error) =>
              toast.error(correctionErrorMessage(error, `Could not hide the ${noun(kind)}`)),
          },
        )
        close()
      }}
    />
  )
}

/** "Hidden by Max" and Restore, with when and why behind the click. */
export function MediaRestoreControl({
  productId,
  kind,
  correction,
}: {
  productId: string
  kind: MediaKind
  correction: MediaCorrection
}) {
  const restore = useSetProductMediaHidden(productId)
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="min-w-0 truncate text-xs text-muted-foreground">
        Hidden by {correction.user_name || 'staff'}
      </span>
      <ActionPopover
        align="end"
        title={`Restore this ${noun(kind)}`}
        trigger={
          <Button variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs">
            Restore
          </Button>
        }
        note={REASON_NOTE}
        confirmLabel="Restore"
        onConfirm={(reason, close) => {
          restore.mutate(
            { kind, mediaId: correction.media_id, hidden: false, reason: reason || null },
            {
              onError: (error) =>
                toast.error(correctionErrorMessage(error, `Could not restore the ${noun(kind)}`)),
            },
          )
          close()
        }}
      >
        <p className="text-xs text-muted-foreground">
          {correctionDetail(correction.user_name, correction.created_at, correction.reason)}
        </p>
      </ActionPopover>
    </span>
  )
}

/** Add a photo or spec sheet, or replace one (the replaced one is hidden with it). */
export function MediaUploadButton({
  productId,
  kind,
  replaces,
}: {
  productId: string
  kind: MediaKind
  replaces?: { id: number; name: string }
}) {
  const upload = useUploadProductMedia(productId)
  const [file, setFile] = useState<File | null>(null)
  const Icon = kind === 'image' ? ImageUp : FileUp
  return (
    <ActionPopover
      align="end"
      title={replaces ? `Replace this ${noun(kind)}` : `Add a ${noun(kind)}`}
      trigger={
        replaces ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground"
            aria-label={`Replace ${replaces.name}`}
            title={`Replace ${noun(kind)}`}
          >
            <Icon className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="outline" size="sm">
            <Upload className="h-4 w-4" />
            Add {noun(kind)}
          </Button>
        )
      }
      note={REASON_NOTE}
      confirmLabel="Upload"
      pending={upload.isPending}
      pendingLabel="Uploading..."
      canConfirm={!!file}
      onOpenChange={(open) => {
        if (!open) setFile(null)
      }}
      onConfirm={(reason, close) => {
        if (!file) return
        upload.mutate(
          { kind, file, reason: reason || null, replacesId: replaces?.id ?? null },
          {
            onSuccess: () => {
              setFile(null)
              close()
            },
            onError: (error) =>
              toast.error(correctionErrorMessage(error, `Could not upload the ${noun(kind)}`)),
          },
        )
      }}
    >
      <Input
        type="file"
        aria-label={kind === 'image' ? 'Photo file' : 'Spec sheet file'}
        accept={kind === 'image' ? 'image/*' : 'application/pdf,image/*'}
        onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        className="h-9 text-sm"
      />
    </ActionPopover>
  )
}

function imageUrl(image: ProductImage) {
  return image.public_url || image.store_url || null
}

/** The product's photos: the store's first, each hideable or replaceable; hidden ones below. */
export function ProductImagesPanel({
  productId,
  images,
  alt,
}: {
  productId: string
  images: ProductImage[]
  alt: string
}) {
  const { data: corrections } = useProductMediaCorrections(productId)
  const shown = images.filter((image) => !image.hidden && imageUrl(image))
  // The product lists only the photos it shows; the ones staff hid come from their hide records.
  const shownIds = new Set(shown.map(mediaIdOf))
  const hidden = staffHiddenRows(corrections, 'image').filter((row) => !shownIds.has(row.media_id))

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <MediaUploadButton productId={productId} kind="image" />
      </div>
      {shown.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {shown.map((image, index) => {
            const id = mediaIdOf(image)
            const name = `photo ${index + 1}`
            return (
              <li key={image.uuid} className="flex flex-col overflow-hidden rounded-lg border bg-white">
                <img
                  src={imageUrl(image) ?? ''}
                  alt={alt}
                  loading="lazy"
                  className="aspect-square w-full object-contain p-2"
                />
                {id != null && (
                  <div className="flex justify-end gap-0.5 border-t bg-card px-1 py-0.5">
                    <MediaUploadButton productId={productId} kind="image" replaces={{ id, name }} />
                    <MediaHideButton productId={productId} kind="image" mediaId={id} name={name} />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="py-12 text-center text-sm text-muted-foreground">No images</p>
      )}
      {hidden.length > 0 && (
        <div className="border-t pt-4">
          <p className="mb-2 text-sm font-medium text-muted-foreground">Not on the store</p>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {hidden.map((correction) => (
              <li
                key={correction.media_id}
                className="flex flex-col overflow-hidden rounded-lg border bg-white"
              >
                {correction.url ? (
                  <img
                    src={correction.url}
                    alt={alt}
                    loading="lazy"
                    className="aspect-square w-full object-contain p-2 opacity-50"
                  />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center">
                    <ImageIcon className="h-7 w-7 text-muted-foreground/30" aria-hidden="true" />
                  </div>
                )}
                <div className="flex min-h-9 items-center border-t bg-card px-2 py-0.5">
                  <MediaRestoreControl productId={productId} kind="image" correction={correction} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
